import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Check, DollarSign } from 'lucide-react';
import { CURRENCIES, useCurrency } from '../context/CurrencyContext';

export default function CurrencySelector() {
  const { currency, setCurrency, info, ratesMeta } = useCurrency();
  const [open, setOpen] = React.useState(false);

  const handleSelect = (code) => {
    setCurrency(code);
    setTimeout(() => setOpen(false), 140);
  };

  const updatedLabel = React.useMemo(() => {
    if (!ratesMeta?.updatedAt) return null;
    try {
      const d = new Date(ratesMeta.updatedAt);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (_e) {
      return null;
    }
  }, [ratesMeta]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          aria-label="Select currency"
          data-testid="currency-selector-trigger"
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md hover:bg-gray-100 transition-colors"
        >
          <DollarSign className="w-4 h-4 text-gray-700" strokeWidth={2} />
          <span className="text-[13px] font-semibold text-gray-800">{info.code}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={10}
        className="w-[320px] max-w-[92vw] p-0 rounded-xl border border-gray-200 shadow-[0_20px_50px_-10px_rgba(0,0,0,0.15)] bg-white"
      >
        <div className="px-5 pt-4 pb-3 border-b border-gray-100">
          <div className="text-[15px] font-medium text-gray-800">Select Currency</div>
          <div className="text-[12px] text-gray-500">Applies across the Zhelta shop</div>
          {updatedLabel && (
            <div
              className="mt-1.5 text-[11px] text-gray-400"
              data-testid="currency-rates-updated"
              title={ratesMeta?.provider ? `Source: ${ratesMeta.provider}` : undefined}
            >
              Rates updated: {updatedLabel}
            </div>
          )}
        </div>
        <div className="max-h-[320px] overflow-y-auto py-2">
          {CURRENCIES.map((c) => {
            const isSelected = currency === c.code;
            return (
              <button
                key={c.code}
                onClick={() => handleSelect(c.code)}
                data-testid={`currency-option-${c.code}`}
                className="w-full flex items-center gap-3 px-5 py-2.5 text-left hover:bg-gray-50 transition-colors"
              >
                <span className="inline-flex items-center justify-center w-9 h-7 rounded bg-gray-50 border border-gray-100 text-[13px] font-semibold text-gray-700">
                  {c.symbol}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-semibold text-gray-900">{c.code}</div>
                  <div className="text-[12px] text-gray-500 truncate">{c.name}</div>
                </div>
                {isSelected && (
                  <Check className="w-4 h-4 text-[#3b82f6] shrink-0" strokeWidth={3} />
                )}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
